import AlertPopup from '../ui/AlertPopup';
import Disaster from './Disaster';
import common from '../common';

export default class Tsunami extends Disaster {
	constructor(world) {
		super(world);

		this.waveHeight = 100;
		this.waveRise = 6;
		this.levelScalar = 0.142;
		this.distance = 106;
		this.fadeDistance = 22;
		this._canUpdate = true;

		this._tiles = [];
		this._headIndex = 0;
		this._waterColors = [1, 2, 3, 4];
		this._destroyed = false;

		let i = 0;
		let left; let
			right;
		let up = this._world.getTile(this._world.columns - 1, this._world.rows - 1);

		while (up) {
			this.initTile(up, i);
			up && this._tiles.push([up]);

			left = up.left;
			while (left) {
				this.initTile(left, i);
				left && this._tiles[i].push(left);
				left = left.left;
			}
			right = up.right;
			while (right) {
				this.initTile(right, i);
				right && this._tiles[i].push(right);
				right = right.right;
			}
			up = up.up;
			++i;
		}

		this._ambientSound = P3.audio.playSound('sfx_ts_ambience_loop_00', { loop: true });
	}

	destroy() {
		this._canUpdate = false;
		this._destroyed = true;
		super.destroy();
	}

	start() {
		if (super.start()) {
			P3.audio.playSound('sfx_ts_disaster_strikes_00');
			P3.utils.delayg(this._tweens, 5, () => {
				for (let i = 0; i < Math.min(this.distance, this._tiles.length); ++i) {
					this.animateWave(i);
				}
			});
		}
	}

	initTile(tile) {
		tile.wave = {
			ground: tile.ground,
			level: tile.level,
			power: 100,
			defenceFactor: 1,
			isFlooded: false,
			isWater: tile.isWater,
		};
	}

	damageStructure(tile) {
		const power = tile.strength + Math.floor(Math.pow(tile.wave.level * this.levelScalar * tile.wave.defenceFactor, 2));

		tile.wave.power = !tile.wave.isWater ? Math.max(5, tile.wave.power - power) : tile.wave.power;
		tile.damaged = tile.wave.power >= tile.strength;
		tile.up && tile.up.wave && (tile.up.wave.power = tile.wave.power);

		const structure = tile.data.structure;

		if (structure) {
			tile.wave.isFlooded = tile.wave.level <= this.waveRise;
			if (structure.population > 0) {
				let type;
				let dead;
				let injured;
				let survive;
				let cost;

				if (Math.round(tile.wave.power * 0.6) > tile.strength || tile.wave.isFlooded) {
					type = AlertPopup.TYPE_DEAD;
					dead = structure.population * 0.5;
					cost = structure.cost * 0.5;
					dead = Math.random() > 0.5 ? Math.ceil(dead) : Math.floor(dead);
					injured = Math.max(0, structure.population - dead);
					survive = 0;
					this._totalDestroyed++;

					this._hospitalDestroyed = structure.special && structure.special === 'hospital';
					this._schoolDestroyed = structure.special && structure.special === 'school';
				} else if (Math.round(tile.wave.power * 0.7) >= tile.strength) {
					type = AlertPopup.TYPE_CRITICAL;
					cost = structure.cost * 0.25;
					dead = structure.population * 0.25;
					dead = Math.random() > 0.5 ? Math.ceil(dead) : Math.floor(dead);
					injured = structure.population - dead;
					survive = 0;
					this._totalDestroyed++;
				} else if (Math.round(tile.wave.power * 0.85) >= tile.strength) {
					type = AlertPopup.TYPE_INJURED;
					dead = 0;
					cost = structure.cost * 0.1;
					injured = structure.population;
					survive = 0;
				} else {
					type = AlertPopup.TYPE_SURVIVE;
					dead = 0;
					cost = 0;
					injured = 0;
					survive = structure.population;
				}
				tile.parent && tile.alert.start(type);

				this._totalDeaths += dead;
				this._totalInjury += injured;
				this._totalSurvive += survive;
				this._totalCost += cost;
			}
		}
	}

	animateWave(index) {
		const t = { value: 0 };
		const length = this._tiles[index].length;
		const delay = 120;

		const rise = new TWEEN.Tween(t, this._tweens)
			.to({ value: 1 }, 20)
			.easing(TWEEN.Easing.Cubic.Out)
			.delay(index * delay)
			.onStart(() => {
				for (let i = 0; i < length; ++i) {
					const tile = this._tiles[index][i];

					tile.ground = 12; // set foam head
					tile.structureSprite.visible = false;
				}
				P3.utils.delayg(this._tweens, delay / 1000, () => {
					for (let i = 0; i < this._tiles[index].length; ++i) {
						const tile = this._tiles[index][i];

						tile.ground = 11; // set foam tail
					}
				});
				P3.utils.delayg(this._tweens, (delay * 2) / 1000, () => {
					for (let i = 0; i < this._tiles[index].length; ++i) {
						const tile = this._tiles[index][i];

						// TODO: what?
						// tile.ground === 11 ? tile.ground = 1 : tile.wave.ground; // set ocean

						if (tile.ground === 11) {
							tile.ground = 1;
						}
					}
				});
			})
			.onUpdate(() => {
				for (let i = 0; i < length; ++i) {
					const tile = this._tiles[index][i];

					tile.level = t.value * this.waveHeight;
				}
			})
			.onComplete(() => {
				for (let i = 0; i < this._tiles[index].length; ++i) {
					const tile = this._tiles[index][i];

					this.damageStructure(tile);
				}
			});

		const fall = new TWEEN.Tween(t, this._tweens)
			.to({ value: 0 }, 540)
			.easing(TWEEN.Easing.Quadratic.InOut)
			.delay(140)
			.onComplete(() => {
				for (let i = 0; i < length; ++i) {
					const tile = this._tiles[index][i];

					tile.ground = tile.wave.ground;
					tile.level = Math.max(this.waveRise, tile.wave.level);
					if (tile.wave.level <= this.waveRise) {
						const h = 1 - Math.round((this.waveRise - tile.wave.level) / this.waveRise);

						tile.ground = this._waterColors[h * (this._waterColors.length - 1)];
					}

					if (tile.data.structure && tile.data.structure.defence) {
						tile.wave.defenceFactor -= tile.data.structure.defence;
					}
					tile.wave.defenceFactor = Math.min(tile.wave.defenceFactor, 0);

					// play destroy animation if building
					(tile.data.structure && tile.data.structure.population > 0 && tile.damaged && tile.parent) && tile.playDestroyAnim();

					// destroy structure if flooded
					tile.data.structure && tile.wave.isFlooded && this.destroyStructure(tile);

					tile.structureSprite.visible = true;
				}
				if (index === Math.min(this.distance, this._tiles.length) - 1) {
					this.end();
				}
			})
			.onUpdate(() => {
				const f = Math.round(Math.max(0, (index - this.fadeDistance) / (this.distance - this.fadeDistance))
                    * this._waterColors.length);

				for (let i = 0; i < length; ++i) {
					const tile = this._tiles[index][i];

					tile.level = Math.max(Math.max(this.waveRise, tile.wave.level), t.value * this.waveHeight);
					tile.ground = this._waterColors[Math.floor((1 - t.value) * f)];
				}
			});

		rise.chain(fall).start();
	}

	end() {
		const unhoused = common.scenario.population.unhoused;

		this._totalDeaths += P3.math.randomInt(0, unhoused); // kills a random amount of unhoused civs
		this._totalDeaths *= Math.max(1 - (common.scenario._ccUpgrades.length / 10), 0.5);
		this._totalDeaths = Math.floor(this._totalDeaths);
		this._totalSurvive = common.scenario.population.total - this._totalDeaths;

		if (!this._destroyed) {
			this.signals.disasterEnd.dispatch(this);
		}
		this._isRunning = false;
		this._canStart = false;
	}
}
